import React from 'react'
import { useSelector } from 'react-redux'
import type { Theme } from '@mui/material'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import { styled, type SxProps } from '@mui/material/styles'
import type { PaletteColor } from '@pbt/pbt-ui-components'
import HtmlContentRenderer from '@pbt/pbt-ui-components/src/components/HtmlContentRenderer'
import { containsHTML } from '@pbt/pbt-ui-components/src/utils'

import Typography from '~/components/elements/Typography/Typography'
import FeatureToggle from '~/constants/featureToggle'
import { getFeatureToggle } from '~/store/reducers/constants'

const StyledQuestionText = styled(Box)(({ theme, ...props }) => ({
  lineHeight: '2.6rem',
  fontWeight: 500,
  color:
    props.color === 'grayGray1'
      ? theme.colors.grayGray1
      : theme.colors.brandPrimary,
}))

const StyledLowAccentText = styled(Box)(({ theme }) => ({
  color: theme.colors.grayGray2,
}))

interface QuestionTitleProps {
  questionColor?: PaletteColor
  questionSubText?: string
  questionSubTextStyle?: SxProps<Theme>
  questionText: string
  questionTextStyle?: SxProps<Theme>
}

const QuestionTitle = ({
  questionColor = 'brandPrimary',
  questionText,
  questionSubText,
  questionTextStyle,
  questionSubTextStyle,
}: QuestionTitleProps) => {
  const isCVCIntakeQuestionnaireEnabled = useSelector(
    getFeatureToggle(FeatureToggle.CVC_INTAKE_QUESTIONNAIRE),
  )
  const TypographyHeadingElement = isCVCIntakeQuestionnaireEnabled
    ? Typography.H3
    : Typography.H1

  const TypographyParagraphElement = isCVCIntakeQuestionnaireEnabled
    ? Typography.ParagraphSm
    : Typography.Paragraph

  return (
    <Grid container direction="column">
      <Grid item>
        {containsHTML(questionText) ? (
          <StyledQuestionText color={questionColor} sx={questionTextStyle}>
            <HtmlContentRenderer content={questionText} />
          </StyledQuestionText>
        ) : (
          <TypographyHeadingElement
            color={questionColor}
            sx={questionTextStyle}
          >
            {questionText}
          </TypographyHeadingElement>
        )}
      </Grid>
      {questionSubText && (
        <Grid item>
          {containsHTML(questionSubText) ? (
            <StyledLowAccentText sx={questionSubTextStyle}>
              <HtmlContentRenderer content={questionSubText} />
            </StyledLowAccentText>
          ) : (
            <TypographyParagraphElement
              color="grayGray2"
              sx={questionSubTextStyle}
            >
              {questionSubText}
            </TypographyParagraphElement>
          )}
        </Grid>
      )}
    </Grid>
  )
}

export default QuestionTitle
